import { ReactNode, useState } from "react";
import styled from "styled-components";

import { ActionBtn } from "@/shared/ui/Btn";
import { Modal } from "@/shared/ui/Modal";

import LoadingSpinner from "../../ui/LoadingSpinner";

const defaultText = "Are you sure?";

/**
 * Hook used to allow easy usage of a confirm dialog
 */
export function useConfirmModal() {
  const [showModal, setShowModal] = useState<boolean>(false);
  const [onConfirm, setOnConfirm] = useState(null);
  const [modalText, setModalText] = useState<ReactNode>(defaultText);
  const [loading, setLoading] = useState<boolean>(false);

  const handleClick = async () => {
    if (onConfirm) {
      await onConfirm();
    }
  };

  let content;

  if (loading) {
    content = <LoadingSpinner />;
  } else {
    content = (
      <>
        <ActionBtn onClick={handleClick}>YES</ActionBtn>
        <ActionBtn onClick={() => setShowModal(false)}>NO</ActionBtn>
      </>
    );
  }

  const modalNode = (
    <Modal isOpen={showModal} onClose={() => setShowModal(false)}>
      <ModalContent>
        {modalText}
        <ButtonBar>{content}</ButtonBar>
      </ModalContent>
    </Modal>
  );

  type withModalConfirmOptions = {
    preventDefault?: boolean;
    forceAsync?: boolean;
  };

  const defaultOptions = {
    preventDefault: false,
    forceAsync: true,
  };

  const withModalConfirm =
    (
      // eslint-disable-next-line
      confirmFn: Function,
      newModalText: ReactNode = defaultText,
      { preventDefault, forceAsync }: withModalConfirmOptions = defaultOptions
    ) =>
    (...args) => {
      // Necessary if we are coming from a form submission, we need to stop the page from refreshing
      if (preventDefault) {
        args[0].preventDefault();
      }
      if (newModalText !== modalText) {
        setModalText(newModalText);
      }
      setOnConfirm(() => async () => {
        const isAsync = confirmFn.constructor.name === "AsyncFunction" || forceAsync;

        if (isAsync) {
          setLoading(true);
          await confirmFn(...args);
          setShowModal(false);
          setLoading(false);
        } else {
          confirmFn(...args);
          setShowModal(false);
        }
      });
      setShowModal(true);
    };

  return {
    showModal,
    setShowModal,
    setModalText,
    modalNode,
    withModalConfirm,
  };
}

const ModalContent = styled("div")`
  display: flex;
  flex-direction: column;
  gap: 20px;
`;

const ButtonBar = styled("div")`
  display: flex;
  justify-content: center;
  gap: 20px;

  button {
    min-width: 110px;
    margin: 0;

    &:nth-child(2) {
      background: var(--color-navy);
    }
  }
`;
